import { ReactNode } from "react";
import { Modal, ModalBody } from "reactstrap";

export interface AnnouncementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Big line at the top. Say what it is, not that there is news. */
  title: ReactNode;
  /** The announcement itself, in a sentence or two. */
  children: ReactNode;
  /**
   * Anything the member can act on without leaving the dialog - the actual
   * control for the thing being announced. This is the point of the format:
   * being told about a setting and being able to change it are the same
   * moment, so nobody has to be sent somewhere to find out what we meant.
   */
  action?: ReactNode;
  /** A second, separated section - a feedback form, say. */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  "data-testid"?: string;
}

/**
 * A centred-across, top-anchored announcement. One thing we have shipped, the
 * control for it, and optionally a question for the member - laid out so it
 * reads as a note from a person rather than a changelog entry.
 *
 * Deliberately not built on InfoDialog: that one always renders a footer of
 * buttons and a bordered header, which is the shape of "confirm or cancel".
 * An announcement has no action to confirm - the whole content IS the action -
 * so it needs no button row, and a heavy header bar makes it read like a
 * system error. Use InfoDialog when you need a decision; this when you have
 * something to say.
 *
 * Not vertically centred, either - see `.announcement-dialog` in _modals.scss
 * for why. The close button sits outside the body on purpose, so it stays put
 * when a long announcement scrolls.
 *
 * Always closeable: never trap someone in an announcement.
 */
const AnnouncementDialog = ({
  isOpen,
  onClose,
  title,
  children,
  action,
  footer,
  size = "md",
  ...rest
}: AnnouncementDialogProps) => (
  <Modal
    isOpen={isOpen}
    toggle={onClose}
    zIndex={1400}
    size={size}
    className="announcement-dialog modal-dialog-scrollable"
    data-testid={rest["data-testid"]}
  >
    <button
      type="button"
      className="close announcement-dialog-close"
      aria-label="Close"
      onClick={onClose}
      data-testid="announcement-dialog-close"
    >
      <span aria-hidden="true">&times;</span>
    </button>

    <ModalBody className="p-4">
      <h4 className="mb-3 pr-4" data-testid="announcement-dialog-title">
        {title}
      </h4>
      <div className="mb-3">{children}</div>

      {action && <div className="mb-2">{action}</div>}

      {footer && (
        <div
          className="mt-4 pt-4 border-top"
          data-testid="announcement-dialog-footer"
        >
          {footer}
        </div>
      )}
    </ModalBody>
  </Modal>
);

export default AnnouncementDialog;
